import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { fetchTiers, createDonationCheckout } from "@/lib/api";

export default function Donate() {
  const [params] = useSearchParams();
  const initialTier = params.get("tier") || "builder";
  const [tiers, setTiers] = useState({});
  const [selectedTier, setSelectedTier] = useState(initialTier);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTiers().then(setTiers).catch(() => {});
  }, []);

  const tierEntries = Object.entries(tiers);
  const isCustom = selectedTier === "custom";

  const onDonate = async () => {
    if (isCustom) {
      const a = parseFloat(customAmount);
      if (!a || a < 1) {
        toast.error("Enter a custom amount of at least £1");
        return;
      }
    }
    setLoading(true);
    try {
      const { url } = await createDonationCheckout({
        tier_id: isCustom ? null : selectedTier,
        custom_amount: isCustom ? parseFloat(customAmount) : null,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        origin_url: window.location.origin,
        message: message || null,
      });
      if (url) window.location.href = url;
      else toast.error("Could not start checkout — please try again.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar showAnnouncement={false} />

      <section className="pt-28 pb-12 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 text-[14px] mb-8" data-testid="donate-back-link">
            <ArrowLeft size={16} /> Back
          </Link>
          <p className="eyebrow text-blue-600">Donate</p>
          <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.02] text-slate-900 text-balance">
            Fund a business. <span className="font-serif-accent text-blue-600">Change a start.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
            100% of your donation goes into the next build. Pick an amount and pay securely with Stripe.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="text-[12px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-4">Choose an amount</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {tierEntries.map(([key, t]) => {
                const active = selectedTier === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedTier(key)}
                    data-testid={`donate-tier-${key}`}
                    className={`text-left p-5 rounded-2xl border transition-all ${
                      active
                        ? "border-blue-600 bg-blue-50/40 shadow-[0_0_0_3px_rgba(37,99,235,0.12)]"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <p className="text-[12px] font-semibold text-blue-600 uppercase tracking-[0.18em]">{t.label}</p>
                    <p className="mt-3 font-display text-3xl font-medium tracking-tight text-slate-900">£{t.amount.toFixed(0)}</p>
                    <p className="mt-2 text-[13px] text-slate-600 leading-snug">{t.description}</p>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setSelectedTier("custom")}
                data-testid="donate-tier-custom"
                className={`text-left p-5 rounded-2xl border transition-all sm:col-span-3 ${
                  isCustom
                    ? "border-blue-600 bg-blue-50/40 shadow-[0_0_0_3px_rgba(37,99,235,0.12)]"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <p className="text-[12px] font-semibold text-blue-600 uppercase tracking-[0.18em]">Custom</p>
                <div className="mt-3 flex items-center gap-3">
                  <span className="font-display text-3xl text-slate-900">£</span>
                  <input
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                    onFocus={() => setSelectedTier("custom")}
                    placeholder="Enter any amount"
                    className="bg-transparent border-0 outline-none font-display text-3xl text-slate-900 w-full"
                    data-testid="donate-custom-input"
                  />
                </div>
                <p className="mt-2 text-[13px] text-slate-600">Set your own amount — every pound counts.</p>
              </button>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[12px] uppercase tracking-[0.18em] font-semibold text-slate-500">Your name (optional)</label>
                <input
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder="So we can thank you"
                  className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-blue-600 focus:bg-white transition"
                  data-testid="donate-name"
                />
              </div>
              <div>
                <label className="text-[12px] uppercase tracking-[0.18em] font-semibold text-slate-500">Email (optional)</label>
                <input
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  placeholder="For your receipt"
                  className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-blue-600 focus:bg-white transition"
                  data-testid="donate-email"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[12px] uppercase tracking-[0.18em] font-semibold text-slate-500">Message (optional)</label>
                <textarea
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="A note to the founder we sponsor"
                  className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-blue-600 focus:bg-white transition resize-none"
                  data-testid="donate-message"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={onDonate}
              disabled={loading}
              data-testid="donate-submit-btn"
              className="btn-primary mt-8 w-full sm:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Redirecting to Stripe..." : "Continue to secure payment"} {!loading && <ArrowRight size={18} />}
            </button>

            <p className="mt-4 text-[13px] text-slate-500 inline-flex items-center gap-2">
              <Lock size={13} /> Payments processed securely by Stripe. We never see your card details.
            </p>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-24 bg-slate-50 border border-slate-100 rounded-3xl p-8">
              <p className="eyebrow text-blue-600">Where it goes</p>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-medium text-slate-900 tracking-tight">
                Every pound, fully accounted.
              </h3>
              <ul className="mt-6 space-y-4">
                {[
                  "100% of donations fund real builds — domain, hosting, design, dev",
                  "Public sponsors wall lists every contributor (unless anonymous)",
                  "Quarterly impact updates show what you helped build",
                  "Receipts emailed to you. Transparent and accountable.",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[14.5px] text-slate-700 leading-relaxed">
                    <Check size={16} className="text-blue-600 mt-1 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
